// Texts to a homeowner from the Finance → Supplements board (Oct 12, 2026). Greg: "When I get the supplement
// invoice and upload it and add the supp amount, I also text the homeowner this message. Can you create a
// button so that it will prefill in a text to the customer?" The button fills the message into a box on the
// card; the person reads it and presses Send, which goes out through `twilio-send-sms` from the company
// number (the same path as the deal's Comms tab) and lands in the deal's comms log with the tag below.

/** The message sent once the completion package and the supplement request have gone to the insurer. Greg's text, with the
 *  signature corrected Oct 19, 2026 (Greg: the company is "Peach Duck Contracting Co. dba Bardin Roofing", never LLC). */
export const SUPPLEMENT_SENT_TEXT = `Bardin Roofing message:
*****NO ACTION REQUIRED AT THIS TIME.*****

We have sent the completion package and supplement request to your insurance to finalize your claim. Supplementation is a standard part of the insurance process. Supplements are additional costs for omitted items or underscoped items on the original estimate.

Any approved supplement funds will be added to the depreciation release and the insurance estimate will be revised to reflect the approved differences. Should you receive final funds/supplements without our notice, please forward the revised estimate to us so we may update our invoice accordingly.

We greatly appreciate the opportunity to work with you. It has been our pleasure and we look forward to closing this project out in a timely manner. Let us know if you have any questions.

Thanks!
Peach Duck Contracting Co.
dba Bardin Roofing`;

/** twilio_messages.tag of that text: the card shows "Texted ✓ <date>" from the latest row with it. */
export const SUPPLEMENT_SENT_TAG = "supplement_sent";

/** The supps stages on which the button shows: from the supplement being sent until the money is collected. */
export const SUPPLEMENT_TEXT_STAGES = new Set<string>([
  "ready_for_supps",
  "ready_for_updated_invoice",
  "supps_waiting_on_final_invoice",
  "supps_waiting_on_claims",
]);

// ── The automatic version (Oct 20, 2026) ─────────────────────────────────────
// The homeowner-comms engine (touch t6) sends the supplement text and email by itself: Supps Sent queues it with a
// 5-minute wait, and the next sweep (every 5 minutes) sends it, 8 AM to 7 PM Eastern. It is tagged like the button
// (supplement_sent), so the card's "Texted ✓" picks it up. Greg: "Can we grey it and put note it's automatic in
// 5 minutes." So the button greys out whenever the engine has it, and comes back when the engine could not send.

/** What the board knows about the engine (finance_settings hc_* keys, deal_comm_settings.mute_all). */
export interface AutoTextEnv {
  mode: string;
  testDealIds: Set<string>;
  disabled: Set<string>;
  muted: Set<string>;
}

/** The deal's homeowner_comm_sends row for t6 (one per deal). */
export interface AutoTextRow {
  status: string;
  skip_reason: string | null;
  sms_status: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
}

export type AutoTextState =
  /** Supps not marked sent yet: the text goes out on its own once they are. */
  | { kind: "will_send" }
  /** Queued, being sent, or held after a burst. */
  | { kind: "pending"; at: string | null; held: boolean; sending: boolean }
  /** The engine texted the homeowner. */
  | { kind: "sent"; at: string | null }
  /** The engine finished without texting: the button comes back. */
  | { kind: "not_sent"; reason: string };

/** Whether the engine sends real texts for this deal right now (the same test as the edge function's). */
export function engineTextsDeal(dealId: string, env: AutoTextEnv): boolean {
  const live = env.mode === "on" || (env.mode === "test" && env.testDealIds.has(dealId));
  return live && !env.disabled.has("t6") && !env.muted.has(dealId);
}

const SKIP_REASONS: Record<string, string> = {
  disabled: "automatic supplement texts are switched off",
  deal_closed: "the deal is closed",
  muted: "automatic texts are muted on this deal",
  stale_event: "Supps Sent was dated more than 3 days back",
  retail: "this is a retail job",
  opted_out: "the homeowner replied STOP",
  no_contact: "there is no phone or email on file",
  no_channel: "there is no valid phone on file",
  deal_missing: "the deal was not found",
};

/** Plain words for why the engine did not text the homeowner. */
export function autoTextReason(row: AutoTextRow): string {
  if (row.status === "failed") return "the text failed to send";
  if (row.status === "shadow") return "automatic texts were in test mode";
  if (row.status === "sent" || row.status === "partial") {
    // The email went; the text did not.
    if (row.sms_status === "opted_out") return "the homeowner replied STOP (the email went out)";
    if (row.sms_status === "no_phone") return "there is no valid phone on file (the email went out)";
    return "the text failed to send (the email went out)";
  }
  const r = row.skip_reason ?? "";
  return SKIP_REASONS[r] ?? (r ? r.replace(/_/g, " ") : "it was skipped");
}

/**
 * The state of the automatic supplement text on one card, or null when there is none and the button works as before:
 * supps sent before the engine went live, the engine off for this deal, or the homeowner already texted by hand
 * (skip reason manual_sent, which the "Texted ✓" chip already shows). A null env (the read failed) also means the old
 * button.
 */
export function supplementAutoText(
  stage: string,
  dealId: string,
  suppsSentDate: string | null,
  env: AutoTextEnv | null,
  row: AutoTextRow | null | undefined,
): AutoTextState | null {
  if (!env) return null;
  if (row) {
    if (row.status === "queued" || row.status === "claimed" || row.status === "held") {
      // Waiting rows do nothing while the engine is off for the deal, so the button comes back.
      if (!engineTextsDeal(dealId, env)) return { kind: "not_sent", reason: "automatic texts are off" };
      return { kind: "pending", at: row.scheduled_for, held: row.status === "held", sending: row.status === "claimed" };
    }
    if ((row.status === "sent" || row.status === "partial") && row.sms_status === "sent") return { kind: "sent", at: row.sent_at };
    if (row.status === "skipped" && row.skip_reason === "manual_sent") return null;
    return { kind: "not_sent", reason: autoTextReason(row) };
  }
  if (stage === "ready_for_supps" && !suppsSentDate && engineTextsDeal(dealId, env)) return { kind: "will_send" };
  return null;
}

/** When a queued text goes out: the first 5-minute sweep at or after it is due. */
export function autoTextEta(scheduledFor: string | null, now: Date = new Date()): Date {
  const due = Math.max(now.getTime(), scheduledFor ? Date.parse(scheduledFor) || 0 : 0);
  const tick = 5 * 60 * 1000;
  return new Date(Math.ceil(due / tick) * tick);
}

/** Whether a Supps Sent date queues the automatic text (the trigger skips dates more than 3 days back, Eastern). */
export function suppsDateTextsAutomatically(sentDate: string, todayET: string): boolean {
  const d = new Date(todayET + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() - 3);
  return sentDate >= d.toISOString().slice(0, 10);
}
